import { Component, Inject, inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { map, Observable, shareReplay, take, tap } from 'rxjs';

// Material Imports
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';

import { RestStop } from '../../../models/rest-stop';
import { Review } from '../../../models/Review';
import { ReviewService } from '../../../services/review.service';
import { StateSnackBarService } from '../../../services/state-snack-bar.service';
import { AuthService } from '../../../services/auth.service';
import { FavoritesService } from '../../../services/favorites.service';
import { ReviewSectionComponent } from '../../review-section/review-section.component';
import { SeoService } from '../../../services/seo.service';
import { Title } from '@angular/platform-browser';
import { RestStopPhotosComponent } from '../rest-stop-photos/rest-stop-photos.component';
import { MatTab, MatTabGroup } from '@angular/material/tabs';

@Component({
  selector: 'app-rest-stop-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatIconModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReviewSectionComponent,
    RestStopPhotosComponent,
    MatTab,
    MatTabGroup,
  ],
  templateUrl: './rest-stop-detail.component.html',
  styleUrls: ['./rest-stop-detail.component.scss'],
})
export class RestStopDetailComponent implements OnInit, OnDestroy {
  @Input() data!: RestStop;

  private reviewService = inject(ReviewService);
  private fb = inject(FormBuilder);
  private snackBarService = inject(StateSnackBarService);
  public authService = inject(AuthService);
  private dialogRef = inject(MatDialogRef<RestStopDetailComponent>, { optional: true });
  private bottomSheetRef = inject(MatBottomSheetRef<RestStopDetailComponent>, { optional: true });
  private seoService = inject(SeoService);
  private titleService = inject(Title);

  reviews$!: Observable<Review[]>;
  otherReviews$!: Observable<Review[]>;

  reviewForm: FormGroup;
  hoverRating = 0;

  public favoritesService = inject(FavoritesService);
  isFavorite$!: Observable<boolean>;

  private ownReviewId: number | null = null;
  public ownReviewStatus: 'approved' | 'pending' | 'none' = 'none';

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData?: RestStop,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) public bottomSheetData?: RestStop,
  ) {
    const injectedData = this.dialogData || this.bottomSheetData;
    if (injectedData) {
      this.data = injectedData;
    }

    this.reviewForm = this.fb.group({
      rating: [0],
      comment: [''],
    });
  }

  ngOnInit(): void {
    this.loadReviews();
    this.isFavorite$ = this.favoritesService.favorites$.pipe(map((favorites) => favorites.includes(this.data.id)));
    this.titleService.setTitle(this.data.name + ' | ' + this.data.autobahn);

    if (this.data) {
      // Update meta description for this rest stop
      this.seoService.updateMetaDescription(this.buildMetaDescription(this.data));
      this.seoService.updateCanonicalUrl(`https://rast-wc.de/${this.data.slug}`);
      const structuredData = {
        '@context': 'https://schema.org/',
        '@type': 'LocalBusiness',
        name: this.data.name,
        //"image": raststaette.imageUrl,
        url: `https://rast-wc.de/${this.data.slug}`,
        geo: {
          '@type': 'GeoCoordinates',
          latitude: this.data.lat,
          longitude: this.data.lng,
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: this.data.averageRating,
          reviewCount: this.data.reviewCount,
        },
      };

      this.seoService.updateStructuredData(structuredData);
    } else {
      console.log('data nicht da');
    }
  }

  loadReviews(): void {
    const currentUserId = this.authService.currentUserValue?.username;

    this.reviews$ = this.reviewService.getReviews(this.data.id).pipe(
      take(1),
      tap((reviews) => {
        const ownReview = reviews.find((r) => r.username === currentUserId);
        if (ownReview) {
          this.ownReviewId = ownReview.id;
          this.reviewForm.patchValue({
            rating: ownReview.rating,
            comment: ownReview.comment,
          });

          if (ownReview.approved) {
            this.ownReviewStatus = 'approved';
            this.reviewForm.enable();
          } else {
            this.ownReviewStatus = 'pending';
            this.reviewForm.disable();
          }
        } else {
          this.ownReviewStatus = 'none';
          this.reviewForm.enable();
        }
      }),
      shareReplay(1),
    );

    this.otherReviews$ = this.reviews$.pipe(map((reviews) => reviews.filter((r) => r.username !== currentUserId)));
  }

  setRating(rating: number): void {
    if (this.ownReviewStatus !== 'pending') {
      this.reviewForm.get('rating')?.setValue(rating);
    }
  }

  submitReview(): void {
    if (this.reviewForm.get('rating')?.value < 1) {
      this.snackBarService.error({ message: 'Bitte gib eine Bewertung von 1-5 Sternen an.' });
      return;
    }

    if (this.reviewForm.invalid) {
      this.snackBarService.error({ message: 'Bitte gib eine Bewertung von 1-5 Sternen an.' });
      return;
    }

    const { rating, comment } = this.reviewForm.value;
    const request$ = this.ownReviewId
      ? this.reviewService.updateReview(this.data.id, this.ownReviewId, rating, comment)
      : this.reviewService.postReview(this.data.id, rating, comment);

    request$.subscribe({
      next: () => {
        const message = this.ownReviewId
          ? 'Deine geänderte Bewertung wird nach Prüfung freigeschaltet.'
          : 'Deine Bewertung wird nach Prüfung freigeschaltet.';
        this.snackBarService.success({ message: `Danke! ${message}` });
        this.closeComponent();
      },
      error: () => {
        this.snackBarService.error({ message: 'Fehler beim Senden der Bewertung.' });
      },
    });
  }

  getStarArray(rating: number): string[] {
    const stars = [];
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.3;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push('star');
      } else if (i === fullStars && halfStar) {
        stars.push('star_half');
      } else {
        stars.push('star_border');
      }
    }
    return stars;
  }

  private closeComponent(): void {
    this.dialogRef?.close();
    this.bottomSheetRef?.dismiss();
  }

  ngOnDestroy(): void {
    // Reset meta description when dialog/sheet is closed or component destroyed
    this.seoService.resetMetaDescription();
    this.seoService.resetCanonicalUrl();
  }

  private buildMetaDescription(stop: RestStop): string {
    const hasReviews = !!(stop.reviewCount && stop.reviewCount > 0);

    let infoPart: string;
    if (hasReviews) {
      infoPart = `${stop.averageRating.toFixed(1)}★ aus ${stop.reviewCount} Bewertungen`;
    } else {
      const details = stop.details;
      const amenityMap: { key: keyof typeof details; label: string }[] = details
        ? [
            { key: 'wifi', label: 'WLAN' },
            { key: 'playground', label: 'Spielplatz' },
            { key: 'chargingStation', label: 'Ladesäule' },
            { key: 'restaurant', label: 'Restaurant' },
            { key: 'gasStation', label: 'Tankstelle' },
            { key: 'shower', label: 'Duschen' },
            { key: 'picnicArea', label: 'Picknickbereich' },
            { key: 'kiosk', label: 'Kiosk' },
            { key: 'atm', label: 'Geldautomat' },
            { key: 'vendingMachine', label: 'Automaten' },
          ]
        : [];

      const amenities: string[] = [];
      if (details) {
        for (const entry of amenityMap) {
          const val = details[entry.key as keyof typeof details];
          if (typeof val === 'boolean' && val) amenities.push(entry.label);
        }
      }
      const top = amenities.slice(0, 3);
      if (top.length > 1) {
        const last = top.pop();
        infoPart = `Ausstattung: ${top.join(', ')} und ${last}`;
      } else if (top.length === 1) {
        infoPart = `Ausstattung: ${top[0]}`;
      } else {
        infoPart = 'Ausstattung ansehen';
      }
    }

    return `Raststätte ${stop.name} an der ${stop.autobahn}: ${infoPart}. Jetzt auf Rast-WC.de prüfen.`;
  }

  get mapsHref(): string {
    if (!this.data) return '#';
    const lat = this.data.lat;
    const lng = this.data.lng;

    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }

  get allAvailableFeatures() {
    const features = [];
    if (!this.data || !this.data.details) return [];

    if (this.data.details.restaurant) features.push({ icon: 'restaurant', label: 'Restaurant' });
    if (this.data.details.gasStation) features.push({ icon: 'local_gas_station', label: 'Tankstelle' });
    if (this.data.details.playground) features.push({ icon: 'child_care', label: 'Spielplatz' });
    if (this.data.details.shower) features.push({ icon: 'shower', label: 'Dusche' });
    if (this.data.details.wifi) features.push({ icon: 'wifi', label: 'WLAN' });
    if (this.data.details.kiosk) features.push({ icon: 'storefront', label: 'Kiosk' });
    if (this.data.details.picnicArea) features.push({ icon: 'deck', label: 'Picknick-Bereich' });
    if (this.data.details.chargingStation) features.push({ icon: 'ev_station', label: 'E-Ladestation' });
    if (this.data.details.atm) features.push({ icon: 'credit_card', label: 'Geldautomat' });

    return features;
  }
}
